import { Button, Flex, Text } from "@minervaproject/fds";
import React, { useContext } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { BreakoutRoomUsers } from "./breakout_room_users";
import { PinnedBreakoutRoom } from "./pinned_breakout_room";
import { StaffUser } from "./staff_user";

import { createBreakoutSessionHelper } from "api/api_helpers";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import {
  PROFILE_PROPERTIES,
  USER_PICKER_PROPERTIES,
  useUsersProperties,
} from "hooks/use_user_properties";
import { endBreakoutSession } from "shared/reducers/classroom";
import { BreakoutType, CapabilityType } from "shared/types";
import { RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { isBreakoutBasedOnPoll } from "utils/breakout";
import { stringComparatorGenerator } from "utils/string";
import { getRoleAndNameForSorting } from "utils/user";

function BreakoutDetails({
  breakout,
  activeBreakoutSessionId,
  hideCallback,
}: {
  breakout: BreakoutType;
  activeBreakoutSessionId?: string;
  hideCallback?: () => void;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const breakoutSessions = useArraySelector(
    classroomSelectors.selectBreakoutSessionsByBreakoutId(breakout.id),
  );
  const breakoutRooms = useArraySelector(
    classroomSelectors.selectBreakoutRoomsByBreakoutId(breakout.id),
  );
  const pinnedBreakoutRooms = breakoutRooms.filter((breakoutRoom) => breakoutRoom.is_pinned);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const isCurrentUserVisitingAnyBreakoutRoom = !!breakoutRooms.find((breakoutRoom) =>
    breakoutRoom.visiting_user_ids?.includes(currentUserId),
  );
  const users = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat(USER_PICKER_PROPERTIES)),
  );
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);

  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canVisitBreakoutsUserIds = useUserIdsWithCapability(CapabilityType.CAN_VISIT_BREAKOUTS);
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);

  const presentStaff = users.filter(({ id, is_present, role }) => {
    if (!is_present) return false;
    const userCanControlClassroom = canControlClassroomUserIds.includes(id);
    const userCanVisitBreakouts = canVisitBreakoutsUserIds.includes(id);
    const userIsVisibleInClass = isVisibleInClassUserIds.includes(id);
    return (
      ((userCanControlClassroom || userCanVisitBreakouts) && userIsVisibleInClass) ||
      role === RoleType.Captioner
    );
  });
  presentStaff.sort(stringComparatorGenerator(getRoleAndNameForSorting));

  const flightAPI = useContext(FlightAPIContext);
  const currentlyBackingUpPoll = useAppSelector(localSelectors.selectCurrentlyBackingUpPoll());
  const isBasedOnPollBeingBackedUp = isBreakoutBasedOnPoll(breakout, currentlyBackingUpPoll);

  const handleStartBreakoutSession = async () => {
    try {
      const serverMilliseconds = flightAPI.getServerMilliseconds();
      await createBreakoutSessionHelper(
        breakout,
        users,
        currentlyBackingUpPoll,
        serverMilliseconds,
        dispatch,
        forumAPI,
      );
    } catch (e) {
      // TODO
    }
  };

  const handleEndBreakoutSession = () => {
    dispatch(endBreakoutSession());
  };

  return (
    <Flex direction="column" none>
      {activeBreakoutSessionId && (
        <div className="pb5">
          <h2 className="body-l text-white pb2">Present Staff</h2>
          <div>
            {presentStaff.map((user) => (
              <StaffUser key={user.id} user={user} breakoutRooms={breakoutRooms} />
            ))}
          </div>
        </div>
      )}
      {pinnedBreakoutRooms.length > 0 && (
        <div className="pb5">
          <Text className="body-l" kind="h2" mb={2}>
            Pinned Breakout Groups
          </Text>
          {pinnedBreakoutRooms.map((breakoutRoom) => (
            <PinnedBreakoutRoom
              key={breakoutRoom.id}
              breakoutRoom={breakoutRoom}
              allUsers={users}
              activeBreakoutSessionId={activeBreakoutSessionId}
              isCurrentUserVisitingAnyBreakoutRoom={isCurrentUserVisitingAnyBreakoutRoom}
            />
          ))}
        </div>
      )}
      <Text className="body-l" kind="h2" mb={2}>
        {activeBreakoutSessionId ? "All Breakout Groups" : breakout.title}
      </Text>
      {breakoutRooms.map((breakoutRoom) => (
        <BreakoutRoomUsers
          key={breakoutRoom.id}
          breakoutRoom={breakoutRoom}
          allUsers={users}
          activeBreakoutSessionId={activeBreakoutSessionId}
        />
      ))}
      {canControlClassroom && !!activeBreakoutSessionId && (
        <Button
          kind="classroom-primary"
          data-testid="end-breakout-button"
          onClick={handleEndBreakoutSession}
          mt={3}
        >
          End Breakout
        </Button>
      )}
      {canControlClassroom && !activeBreakoutSessionId && (
        <>
          <div
            data-tooltip-content={
              isBasedOnPollBeingBackedUp
                ? "Temporarily disabled while the corresponding poll session is being saved"
                : undefined
            }
            data-tooltip-id="breakout-button-tooltip"
            className="mt3"
          >
            <Button
              kind="classroom-primary"
              data-testid="start-breakout-session-button"
              onClick={handleStartBreakoutSession}
              disabled={isBasedOnPollBeingBackedUp}
            >
              {breakoutSessions.length ? "Restart" : "Start"} Breakout
            </Button>
          </div>
          <Button
            kind="text-classroom-secondary"
            className="justify-center"
            onClick={hideCallback}
            mt={2}
          >
            Back
          </Button>
          {isBasedOnPollBeingBackedUp && <ReactTooltip id="breakout-button-tooltip" />}
        </>
      )}
    </Flex>
  );
}

export { BreakoutDetails };
